// EntitlementsStore — customer-admin-facing state for the /admin/entitlements/* subtree.
// Backed by rd's new entitlements endpoints (slice 1).
//
// Mirrors the UserStore + WorkspaceStore conventions: class + makeAutoObservable,
// async actions return promises so callers can chain UI feedback.

import { makeAutoObservable, runInAction } from "mobx";

import {
  fetchTenantEntitlements,
  fetchMyEntitlements,
  grantEntitlement as apiGrant,
  revokeEntitlement as apiRevoke,
} from "../queries/entitlements";

// Catalog of SKUs we support. Mirrors KNOWN_FEATURES in rd's validation.py.
// Slice 1 has 4; slice 2+ may add more.
//
// PLEX-6514: renamed from the "v2"/"coach" naming — "v2" became "workspace"
// (it's the root gate for the whole project/workspace feature suite below),
// "v2-data" became "project-files" and "v2-share" became "artifact-share"
// (the bare words were too generic), everything else just dropped the "v2-"
// prefix. `parent` mirrors rd's FEATURE_PARENT (PLEX-6376) — cosmetic here,
// used only to grey out/warn in the admin grant UI; backend stays authoritative.
export const CATALOG = [
  { key: "standard",    name: "Standard",     description: "Base platform — search, graph analysis, basic chat",         seats: 500, pricePerSeat:  300, type: "tenant-wide" },
  { key: "workspace",   name: "Workspace",    description: "Research-assistant agent with cross-thread memory",          seats: 350, pricePerSeat:  500, type: "per-user" },
  { key: "plex-cp",     name: "Plex CP",      description: "Chemoproteomics — target identification + SAR",              seats: 200, pricePerSeat: 1000, type: "per-user" },
  { key: "proteo-chem", name: "ProteoChem",   description: "Proteomic-chemoinformatic data fusion + indications",         seats:  80, pricePerSeat:  500, type: "per-user" },
  { key: "notebook",    name: "Notebook",     description: "Project notebook drawer — findings & open questions", type: "per-user", v2Default: false },
  { key: "project-files", name: "Project Files", description: "File upload/management drawer",                     type: "per-user", v2Default: true },
  { key: "memory",      name: "Memory",       description: "Workspace memory drawer",                             type: "per-user", v2Default: true },
  { key: "team",        name: "Team",         description: "Team roster & membership drawer",                     type: "per-user", v2Default: false },
  { key: "artifact-share", name: "Artifact Share", description: "Share a pinned artifact",                        type: "per-user", v2Default: true, parent: "workspace" },
  { key: "custom-widgets", name: "Custom Widgets", description: "LLM-authored interactive widgets in chat",         type: "per-user", v2Default: false, parent: "workspace" },
  { key: "code-execution", name: "Code (Beta)", description: "Workspace code execution — run Python over your data", type: "per-user", v2Default: false, parent: "workspace" },
  { key: "ab-compare",  name: "A/B Compare",  description: "Internal only — analyst A/B experiment harness (Plex staff)", type: "per-user", v2Default: false },
];

// Client-side mirror of the backend FEATURE_DEFAULTS — used ONLY for the
// pre-load window so default-on buttons don't flicker off before /me
// resolves. Absent ⇒ false. After loadMine(), server truth is authoritative.
export const V2_FEATURE_DEFAULTS = {
  "project-files": true,
  "memory": true,
  "artifact-share": true,
};

const TENANT_WIDE = "*";

class EntitlementsStore {
  // Raw grants from the API — one record per (tenant_id, user_id, feature).
  grants = [];

  // UI state
  loading = false;
  error = null;
  lastFetchedAt = null;

  // Inflight write tracker so the UI can show per-action spinners.
  // Keyed by `${user_id}#${feature}`.
  pendingWrites = new Set();

  // End-user effective feature set from GET /api/me/entitlements.
  myFeatures = new Set();
  myLoaded = false;
  myLoading = false;

  constructor(rootStore) {
    this.root = rootStore;
    makeAutoObservable(this, { root: false });
  }

  get authData() {
    return this.root?.userStore?.authData;
  }

  // ─── Derived ─────────────────────────────────────────────────────────

  /** Grants grouped by feature key — used by the SKU view. */
  get grantsByFeature() {
    const out = {};
    for (const g of this.grants) {
      (out[g.feature] = out[g.feature] || []).push(g);
    }
    return out;
  }

  /** Per-SKU summary: seats assigned, of total. */
  get skuSummary() {
    return CATALOG.map((sku) => {
      const grants = this.grantsByFeature[sku.key] || [];
      // Tenant-wide grants count as "everyone" — for display we show 1 grant
      // but the seat utilization is effectively N (tenant total, unknown here).
      const tenantWide = grants.some((g) => g.user_id === TENANT_WIDE);
      const perUserAssigned = grants.filter((g) => g.user_id !== TENANT_WIDE).length;
      return {
        ...sku,
        assigned: perUserAssigned,
        tenantWide,
        // Next expiry — find the earliest non-null expires_at
        nextExpiry: grants
          .map((g) => g.expires_at)
          .filter(Boolean)
          .sort()[0] || null,
      };
    });
  }

  // ─── Async actions ───────────────────────────────────────────────────

  /** True if the current user should see the given feature. Before /me
   *  resolves, falls back to the client default map (anti-flicker). */
  isFeatureEnabled(key) {
    if (this.myLoaded) return this.myFeatures.has(key);
    return !!V2_FEATURE_DEFAULTS[key];
  }

  /** Load the end-user's effective features. Idempotent — fetches once. */
  async loadMine() {
    if (!this.authData || this.myLoaded || this.myLoading) return;
    this.myLoading = true;
    try {
      const resp = await fetchMyEntitlements(this.authData);
      // The API returns a {status, content:{features}} envelope (RequestBuilder
      // doesn't unwrap it); tolerate an unwrapped shape too, for safety.
      const features = resp?.content?.features ?? resp?.features ?? [];
      runInAction(() => {
        this.myFeatures = new Set(features);
        this.myLoaded = true;
        this.myLoading = false;
      });
    } catch (err) {
      console.error("EntitlementsStore.loadMine failed", err);
      runInAction(() => { this.myLoading = false; });
    }
  }

  async load() {
    if (!this.authData) return;
    this.loading = true;
    this.error = null;
    try {
      const resp = await fetchTenantEntitlements(this.authData);
      // API wraps the payload in {status, content:{entitlements}}.
      const items = resp?.content?.entitlements ?? resp?.entitlements ?? [];
      runInAction(() => {
        this.grants = items;
        this.lastFetchedAt = new Date();
        this.loading = false;
      });
    } catch (err) {
      console.error("EntitlementsStore.load failed", err);
      runInAction(() => {
        this.error = err.message || String(err);
        this.loading = false;
      });
    }
  }

  async grant({ user_id, feature, expires_at = null, cap_usd_monthly = null, effect = "allow" }) {
    const key = `${user_id}#${feature}`;
    this.pendingWrites.add(key);
    try {
      const payload = {
        user_id,
        feature,
        granted_by: this.root.userStore.userName,
        expires_at,
        cap_usd_monthly,
        effect,
        source: "admin-ui",
      };
      const resp = await apiGrant(this.authData, payload);
      // API wraps the new row in {status, content:<record>}.
      const record = resp?.content ?? resp;
      runInAction(() => {
        // Optimistic-ish: replace any existing local copy, push the fresh one
        this.grants = this.grants.filter(
          (g) => !(g.user_id === user_id && g.feature === feature),
        );
        this.grants.push(record);
      });
      return record;
    } finally {
      runInAction(() => this.pendingWrites.delete(key));
    }
  }

  async revoke({ user_id, feature }) {
    const key = `${user_id}#${feature}`;
    this.pendingWrites.add(key);
    try {
      await apiRevoke(this.authData, user_id, feature);
      runInAction(() => {
        this.grants = this.grants.filter(
          (g) => !(g.user_id === user_id && g.feature === feature),
        );
      });
    } finally {
      runInAction(() => this.pendingWrites.delete(key));
    }
  }

  isPending(user_id, feature) {
    return this.pendingWrites.has(`${user_id}#${feature}`);
  }
}

export default EntitlementsStore;
